import type { ProductCardDataShows } from '../transforms/shows'
import type { ProductDetailsDataShows } from '../transforms/shows/details'
import type { PageContext } from './base'

/**
 * Filtering predicates - only the ones we actually use
 * Uses same structure as client for areas filtering
 */
const filtersPredicatesByGroup = {
  // By areas (region) - used for region filtering
  areas: (item: ProductDetailsDataShows, selected: string[]) => {
    return selected.includes(item.filters.areas.name)
  },

  // By show name - used for show name filtering
  showName: (item: ProductDetailsDataShows, selected: string[]) => {
    return selected.includes(item.filters.showName.name)
  },
}

export const filtersPredicatesShows = filtersPredicatesByGroup as Record<
  string,
  (arg0: ProductDetailsDataShows, arg1: string[]) => boolean
>

/**
 * Filters ProductDetailsDataShows by region using predicate (same as client)
 * For central: only 'מרכז' (not Sharon)
 * For Sharon: only 'השרון'
 */
const filterDetailsByRegion = (
  details: ProductDetailsDataShows[],
  region: string,
): { filtered: ProductDetailsDataShows[]; isFallback: boolean } => {
  const areasPredicate = filtersPredicatesShows.areas

  if (!areasPredicate) {
    return { filtered: details, isFallback: false }
  }

  if (region === 'central') {
    // Central: 'תל אביב והמרכז' (the actual area name in the data)
    const centralFiltered = details.filter((item) => {
      return areasPredicate(item, ['תל אביב והמרכז'])
    })

    if (centralFiltered.length > 0) {
      return { filtered: centralFiltered, isFallback: false }
    }

    // Fallback to Sharon only
    const sharonFiltered = details.filter((item) => {
      return areasPredicate(item, ['השרון'])
    })

    if (sharonFiltered.length > 0) {
      return { filtered: sharonFiltered, isFallback: true }
    }

    // No results
    return { filtered: [], isFallback: false }
  }

  // For other regions, use MV `areaName` from shows catalog (see region-map MV list)
  const regionVariations: Record<string, string[]> = {
    sharon: ['השרון'],
    north: ['חיפה והצפון'],
    south: ['באר שבע והדרום'],
    jerusalem: ['ירושלים'],
    shfela: ['איזור השפלה'],
    telaviv: ['תל אביב'],
  }

  const selectedAreas = regionVariations[region.toLowerCase()] || [region]

  const filtered = details.filter((item) => {
    return areasPredicate(item, selectedAreas)
  })

  return { filtered, isFallback: false }
}

/**
 * Filters ProductDetailsDataShows by city
 */
const filterDetailsByCity = (details: ProductDetailsDataShows[], city: string): ProductDetailsDataShows[] => {
  const cityLower = city.toLowerCase()

  return details.filter((item) => {
    const placeName = item.computed.placeName?.toLowerCase() || ''

    return placeName.includes(cityLower)
  })
}

/**
 * Filters ProductDetailsDataShows by venue
 */
const filterDetailsByVenue = (details: ProductDetailsDataShows[], venue: string): ProductDetailsDataShows[] => {
  const venueLower = venue.toLowerCase()

  return details.filter((item) => {
    const placeName = item.computed.placeName?.toLowerCase() || ''

    return placeName.includes(venueLower)
  })
}

/**
 * Filters ProductDetailsDataShows by show name
 */
const filterDetailsByShowName = (details: ProductDetailsDataShows[], showName: string): ProductDetailsDataShows[] => {
  const showNamePredicate = filtersPredicatesShows.showName

  if (!showNamePredicate) {
    return details
  }

  return details.filter((item) => {
    return showNamePredicate(item, [showName])
  })
}

/**
 * Filters ProductDetailsDataShows based on page context
 * Implements fallback chain: central (מרכז only) → Sharon (השרון only) → empty
 * Returns filtered details and fallback status
 */
export const filterShowsDetailsByContext = (
  details: ProductDetailsDataShows[],
  context: PageContext,
): { filtered: ProductDetailsDataShows[]; isSharonFallback: boolean } => {
  if (!context.region && !context.city && !context.venue && !context.showName) {
    return { filtered: details, isSharonFallback: false }
  }

  let filtered = details
  let isSharonFallback = false

  if (context.region) {
    const regionResult = filterDetailsByRegion(filtered, context.region)

    filtered = regionResult.filtered
    isSharonFallback = regionResult.isFallback
  }

  if (context.city && filtered.length > 0) {
    const cityFiltered = filterDetailsByCity(filtered, context.city)

    if (cityFiltered.length > 0) {
      filtered = cityFiltered
    }
  }

  if (context.venue && filtered.length > 0) {
    const venueFiltered = filterDetailsByVenue(filtered, context.venue)

    if (venueFiltered.length > 0) {
      filtered = venueFiltered
    }
  }

  if (context.showName && filtered.length > 0) {
    const showFiltered = filterDetailsByShowName(filtered, context.showName)

    if (showFiltered.length > 0) {
      filtered = showFiltered
    }
  }

  return { filtered, isSharonFallback }
}

/**
 * Legacy function for backward compatibility - filters cards by context
 * This is kept for any code that still uses card-based filtering
 */
export const filterShowsByContext = (cards: ProductCardDataShows[], context: PageContext): ProductCardDataShows[] => {
  if (!context.region && !context.city && !context.venue && !context.showName) {
    return cards
  }

  let filtered = cards

  if (context.city) {
    const cityLower = context.city.toLowerCase()

    const cityFiltered = filtered.filter((card) => {
      const placeName = card.computed.locationLabel?.toLowerCase() || ''

      return placeName.includes(cityLower)
    })

    if (cityFiltered.length > 0) {
      filtered = cityFiltered
    }
  }

  if (context.venue) {
    const venueLower = context.venue.toLowerCase()

    const venueFiltered = filtered.filter((card) => {
      const placeName = card.computed.locationLabel?.toLowerCase() || ''

      return placeName.includes(venueLower)
    })

    if (venueFiltered.length > 0) {
      filtered = venueFiltered
    }
  }

  if (context.showName) {
    const showNameLower = context.showName.toLowerCase()

    const showFiltered = filtered.filter((card) => {
      const title = card.computed.title?.toLowerCase() || ''

      return title.includes(showNameLower)
    })

    if (showFiltered.length > 0) {
      filtered = showFiltered
    }
  }

  return filtered
}
